<script>
import Layout from '@layouts/loginLayout.vue'
import { authComputed } from '@state/helpers'
import { submitExpiredPasswordForm } from '@src/api/auth'
import _ from 'lodash'

export default {
  page: {
    title: 'Expired Password',
    meta: [
      {
        name: 'description',
        content: 'The Expired Password page.',
      },
    ],
  },
  components: { Layout },
  data() {
    return {
      sessionToken: '',
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
      initialLoading: true,
      loading: false,
      successfully: false,
    }
  },
  computed: {
    ...authComputed,

    placeholders() {
      return {
        oldPassword: 'Old Password',
        newPassword: 'New Password',
        confirmPassword: 'Confirm New Password',
      }
    },

    canPressButton() {
      if (!this.oldPassword || !this.newPassword || !this.confirmPassword) {
        return false
      }

      return !this.loading
    },

    confirmPasswordInvalid() {
      return this.newPassword !== this.confirmPassword
    },

    samePasswordInvalid() {
      return this.newPassword === this.oldPassword
    },
  },
  mounted() {
    this.loadExpiredPasswordForm()
  },
  methods: {
    async loadExpiredPasswordForm() {
      if (!this.stateToken) {
        await this.$router.push({
          name: 'login',
        })
        return
      }
      this.sessionToken = this.stateToken
      this.initialLoading = false
      this.authError = 'Your password has expired. Please set a new password.'
      this.toastError()
    },

    async submitExpiredPasswordForm() {
      this.authError = null
      if (this.confirmPasswordInvalid) {
        this.authError = 'The password and confirm password fields do not match.'
        this.toastError()
        return
      }
      if (this.samePasswordInvalid) {
        this.authError = 'Your new password cannot be your old password.'
        this.toastError()
        return
      }
      this.loading = true
      try {
        await submitExpiredPasswordForm({
          oldPassword: this.oldPassword,
          newPassword: this.newPassword,
          sessionToken: this.sessionToken,
        }).then(() => {
          this.$router.push({
            name: 'login',
            params: { message: 'You have successfully reset your password!' },
          })
        })
      } catch (err) {
        this.authError = 'Server Error. Please try again in a few moments.'
        const response = _.get(err, 'response.data.data', 'Server Error. Please try again in a few moments.')
        if (typeof response === 'string') {
          this.authError = response
        }
        if (typeof response === 'object') {
          if (typeof response.password === 'object') {
            this.authError = response.password.join(' ')
          }
          if (typeof response.newPassword === 'object') {
            this.authError = response.newPassword.join(' ')
          }
        }
        this.authError = this.authError.replace('Okta Error: password.value: ', '')
        this.authError = this.authError.replace('Okta Error: newPassword.value: ', '')
        this.authError = this.authError.replace('Okta Error: ', '')
        this.toastError()
        this.loading = false
      }
    },

    toastError() {
      this.$bvToast.toast(this.authError, {
        toaster: 'b-toaster-bottom-center',
        toastClass: 'toast-error',
      })
    },
  },
}
</script>

<template>
  <Layout>
    <form @submit.prevent="submitExpiredPasswordForm">
      <h1 class="motto-blue-text mb-55">Expired Password</h1>

      <BaseIcon v-if="initialLoading" name="spinner" spin />

      <div v-else-if="!successfully">
        <BaseFormGroup :label="placeholders.oldPassword" class="mb-40">
          <BaseInputPassword
            v-model="oldPassword"
            name="oldPassword"
            type="text"
            :placeholder="placeholders.oldPassword" />
        </BaseFormGroup>

        <BaseFormGroup :label="placeholders.newPassword" class="mb-40">
          <BaseInputPassword
            v-model="newPassword"
            name="newPassword"
            type="text"
            :placeholder="placeholders.newPassword" />
        </BaseFormGroup>

        <BaseFormGroup :label="placeholders.confirmPassword">
          <BaseInputPassword
            v-model="confirmPassword"
            name="confirmPassword"
            type="text"
            :placeholder="placeholders.confirmPassword" />
        </BaseFormGroup>

        <BaseLink :to="{ name: 'login' }">Back to Login</BaseLink>

        <BaseButton :disabled="!canPressButton" type="submit">
          <BaseIcon v-if="loading" name="spinner" spin />
          <span v-else>Set New Password</span>
        </BaseButton>
      </div>

      <div v-else>
        <BaseLink :to="{ name: 'login' }">
          <BaseButton type="button">
            <span>Back to Login</span>
          </BaseButton>
        </BaseLink>
      </div>
    </form>
  </Layout>
</template>

<style lang="scss" module>
@import '@design';
</style>
